import axios from "axios";
import { CheckCircle, Edit, MoreVertical, Trash2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

const AdminPaymentQR = () => {
  const [qrs, setQrs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionOpen, setActionOpen] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQR, setEditingQR] = useState(null);

  const fetchQRs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/payment-qr/get-all`,
      );
      setQrs(res.data.data || []);
    } catch (err) {
      showError("Failed to fetch payment QRs");
      setQrs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQRs();
  }, []);

  const handleDelete = async (qr) => {
    const confirmed = await confirmAction({
      title: "Delete QR?",
      text: "This will permanently remove the UPI QR.",
      confirmText: "Delete",
    });

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/payment-qr/delete/${qr._id}`,
      );
      showSuccess("QR deleted");
      fetchQRs();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to delete QR");
    }
  };

  const toggleActivation = async (qr) => {
    try {
      const url = qr.isActive
        ? `/payment-qr/deactivate/${qr._id}`
        : `/payment-qr/activate/${qr._id}`;

      await axios.patch(`${process.env.REACT_APP_API_URL}${url}`);
      showSuccess(qr.isActive ? "QR deactivated" : "QR activated");
      fetchQRs();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to toggle status");
    }
  };

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className=" py-2">
        <Breadcrumb
          title1="Payments"
          title2="Payment UPI"
          showBtn
          buttonName="Add upi"
          onBtnClick={() => {
            setEditingQR(null);
            setModalOpen(true);
          }}
        />
      </div>

      <div className=" mb-6 bg-white rounded-xl shadow-sm  p-6">
        {loading ? (
          <PaymentQRSkeleton />
        ) : qrs.length === 0 ? (
          <div className="py-20 text-center text-gray-500">
            No payment QR found
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qrs.map((qr) => (
              <div
                key={qr._id}
                className="relative rounded-2xl border
    bg-white
    p-5
    transition
    hover:shadow-lg
    "
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 leading-tight">
                      {qr.label}
                    </h3>
                    <p className="mt-1 text-xs font-mono text-gray-500 break-all">
                      {qr.upiId}
                    </p>
                  </div>

                  <button
                    onClick={() =>
                      setActionOpen((p) => (p === qr._id ? null : qr._id))
                    }
                    className="p-2 rounded-lg hover:bg-gray-100 transition"
                  >
                    <MoreVertical size={18} />
                  </button>
                </div>

                {/* Divider */}
                <div className="my-4 h-px bg-gray-100" />

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <div
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold
        ${
          qr.isActive
            ? "bg-green-50 text-green-700"
            : "bg-gray-100 text-gray-500"
        }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full
          ${qr.isActive ? "bg-green-500" : "bg-gray-400"}`}
                    />
                    {qr.isActive ? "Active" : "Inactive"}
                  </div>

                  <span className="text-xs text-gray-400 tracking-wide">
                    UPI QR
                  </span>
                </div>

                {/* Actions */}
                {actionOpen === qr._id && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setActionOpen(null)}
                    />

                    <div
                      className="absolute right-3 top-14 z-20 w-44 overflow-hidden
        rounded-xl border bg-white shadow-xl"
                    >
                      <button
                        onClick={() => {
                          setEditingQR(qr);
                          setModalOpen(true);
                          setActionOpen(null);
                        }}
                        className="w-full px-4 py-3 text-left flex items-center gap-2
            hover:bg-gray-100 transition"
                      >
                        <Edit size={16} /> Edit
                      </button>

                      <button
                        onClick={() => toggleActivation(qr)}
                        className="w-full px-4 py-3 text-left flex items-center gap-2
            hover:bg-gray-100 transition"
                      >
                        {qr.isActive ? (
                          <>
                            <XCircle size={16} /> Deactivate
                          </>
                        ) : (
                          <>
                            <CheckCircle size={16} /> Activate
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleDelete(qr)}
                        className="w-full px-4 py-3 text-left flex items-center gap-2
            text-red-600 hover:bg-red-50 transition"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <PaymentQRModal
        key={editingQR?._id || "create"}
        isOpen={modalOpen}
        editingQR={editingQR}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchQRs}
      />
    </div>
  );
};

const PaymentQRModal = ({ isOpen, onClose, onSuccess, editingQR }) => {
  const [label, setLabel] = useState("");
  const [upiId, setUpiId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (editingQR) {
      setLabel(editingQR.label || "");
      setUpiId(editingQR.upiId || "");
    } else {
      setLabel("");
      setUpiId("");
    }
  }, [isOpen, editingQR]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!label || !upiId) {
      return showError("All fields required");
    }

    try {
      setLoading(true);

      if (editingQR) {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/payment-qr/update/${editingQR._id}`,
          { label, upiId },
        );
        showSuccess("UPI updated");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/payment-qr/create`, {
          label,
          upiId,
        });
        showSuccess("UPI created");
      }

      onClose();
      onSuccess();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to save UPI");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          {editingQR ? "Edit UPI" : "Add UPI"}
        </h3>

        <input
          className="w-full rounded-lg px-4 py-2 mb-3
            border border-gray-300
            focus:border-accentTextColor
            focus:outline-none transition"
          placeholder="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />

        <input
          className="w-full rounded-lg px-4 py-2
            border border-gray-300
            focus:border-accentTextColor
            focus:outline-none transition"
          placeholder="UPI ID"
          value={upiId}
          onChange={(e) => setUpiId(e.target.value)}
        />

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg">
            Cancel
          </button>
          <button
            disabled={loading}
            onClick={handleSubmit}
            className="px-4 py-2 bg-primaryBtnColor text-white rounded-lg disabled:opacity-50"
          >
            {loading ? "Saving..." : editingQR ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentQRSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="border rounded-xl p-4 animate-pulse bg-gray-50">
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-6" />
        <div className="h-6 bg-gray-200 rounded w-20" />
      </div>
    ))}
  </div>
);

export default AdminPaymentQR;
